import { KeyboardEventHandler, useRef, useState } from 'react';

import { css } from '@emotion/react';
import {
  Accordion,
  Box,
  getCSSVariable,
  Image,
  ImageProps,
  Link,
  Text,
} from '@opendoor/bricks-next';

import { ILpComponentMulticardFields } from 'declarations/contentful';

import { ICtaStyles, LPFauxBtn } from '../../landing-pages-v2/shared/Buttons';
import Sheet from '../../landing-pages-v2/shared/Sheet';
import { IComponentThemeOptions } from '../../landing-pages-v2/shared/styles/ComponentTheme';
import { Eyebrow } from '../../landing-pages-v2/shared/Typography';
import { markdownToReactElement } from '../../landing-pages-v2/shared/utils';

const convertMinHeight = (value: ILpComponentMulticardFields['minHeight']): number | undefined => {
  if (!value) return undefined;
  return parseInt(value.replace('px', ''), 10);
};
export interface IMulticardCardDetails {
  eyebrow?: string;
  title?: string;
  titleSize?: 'small' | 'medium' | 'large';
  subhead?: string;
  desc?: string;
  imageUrl?: string;
  ctaDisplayText?: string;
  ctaStyle?: ICtaStyles;
  ctaSheetLabel?: string;
  ctaSheet?: any;
  componentTheme: IComponentThemeOptions;
  isArched?: boolean;
  isAccordion?: boolean;
  cardStyle?: string;
  cardVerticalAlignment?: string;
  analyticsId?: string;
  redirectUrl?: string;
  cardsLimit?: number;
  columnNumber?: number;
  backgroundImageSize?: 'cover' | 'contain';
  backgroundColor?: string;
  imageLayout?: 'top' | 'bottom';
}

const TitleSizeTokens: Record<string, '$bodyXlarge' | '$subheaderXsmall' | '$subheaderSmall'> = {
  small: '$bodyXlarge',
  medium: '$subheaderXsmall',
  large: '$subheaderSmall',
};

interface IMulticardCardProps<T> {
  id?: string;
  card: T;
  componentTheme: IComponentThemeOptions;
  isArched?: boolean;
  isAccordion?: boolean;
  minHeight?: ILpComponentMulticardFields['minHeight'];
  cardVerticalAlignment?: string;
  columnNumber?: number;
}

interface ICardSheet {
  card: IMulticardCardDetails;
  isArched?: boolean;
  componentTheme: IComponentThemeOptions;
  cardVerticalAlignment?: string;
  minHeight?: string;
  columnNumber?: number;
}

export const getCardArch = ({
  cardStyle,
  isArched,
}: {
  cardStyle?: string;
  isArched?: boolean;
}) => {
  if (cardStyle === 'Arch') {
    return true;
  }

  return isArched;
};

interface ICardImage extends Omit<ImageProps, 'variant' | 'orientation' | 'source'> {
  isArched: boolean;
  src: string;
  alt: string;
  columnNumber?: number;
  backgroundImageSize?: 'cover' | 'contain';
}
export const CardImage = ({
  isArched,
  src,
  alt,
  columnNumber,
  backgroundImageSize,
  ...props
}: ICardImage) => {
  return (
    // eslint-disable-next-line @opendoor/no-html-elements
    <div
      css={css`
        div {
          background-size: ${backgroundImageSize || 'cover'};
        }
      `}
    >
      <Image
        {...props}
        source={{
          uri: src,
          height: 180,
        }}
        flex={1}
        minHeight={160}
        $largerThanMD={{ minHeight: 320 }}
        $largerThanSM={{ minHeight: 200 }}
        // eslint-disable-next-line no-nested-ternary -- Grandfathered violation
        height={columnNumber ? (columnNumber > 3 ? 180 : 240) : 180}
        alt={alt}
        marginLeft={-16}
        maxWidth="unset"
        width={'calc(100% + 32px)' as any}
        borderTopLeftRadius={isArched ? '$full' : '$12x'}
        borderTopRightRadius={isArched ? '$full' : '$12x'}
        borderBottomLeftRadius="$2x"
        borderBottomRightRadius="$2x"
      />
    </div>
  );
};

const MulticardCard = ({
  id,
  card,
  isArched,
  minHeight,
  componentTheme,
  cardVerticalAlignment,
  isAccordion,
  columnNumber,
}: IMulticardCardProps<IMulticardCardDetails>) => {
  return (card.ctaSheet || card.redirectUrl) && !isAccordion ? (
    <CardSheet
      card={card}
      minHeight={minHeight}
      isArched={getCardArch({ cardStyle: card.cardStyle, isArched })}
      componentTheme={componentTheme}
      cardVerticalAlignment={cardVerticalAlignment}
      columnNumber={columnNumber}
    />
  ) : (
    <Box
      key={id}
      display="flex"
      minHeight={minHeight === 'auto' ? 'auto' : convertMinHeight(minHeight)}
      overflow="hidden"
      borderTopLeftRadius={isArched ? '$full' : '$12x'}
      borderTopRightRadius={isArched ? '$full' : '$12x'}
      borderBottomLeftRadius={'$12x'}
      borderBottomRightRadius={'$12x'}
      // eslint-disable-next-line no-nested-ternary -- Grandfathered violation
      paddingTop={card.imageUrl ? '$4x' : cardVerticalAlignment === 'center' ? '$12x' : '$44x'}
      paddingBottom={'$12x'}
      paddingLeft={'$12x'}
      paddingRight={'$12x'}
      backgroundColor={getCSSVariable('$backgroundTertiary', 'color')}
    >
      <MulticardCardDetails
        {...card}
        isArched={getCardArch({ cardStyle: card.cardStyle, isArched })}
        cardVerticalAlignment={cardVerticalAlignment}
        componentTheme={componentTheme}
        redirectUrl={card.redirectUrl}
        isAccordion={isAccordion}
      />
    </Box>
  );
};

const CardSheet = ({
  card,
  isArched,
  componentTheme,
  cardVerticalAlignment,
  columnNumber,
  minHeight,
}: ICardSheet) => {
  const [isVisible, setIsVisible] = useState(false);
  const ctaRef = useRef<HTMLAnchorElement>(null);
  // When animation is complete
  const exitComplete = () => {
    if (card.ctaSheet) {
      ctaRef.current?.focus();
    }
  };

  // Make visual buttons work like Buttons
  const onKeyDown: KeyboardEventHandler<HTMLAnchorElement> = (e) => {
    if (card.redirectUrl && !card.ctaSheet) {
      e.stopPropagation();
    } else if (e.key === ' ' || e.key === 'Spacebar') {
      setIsVisible(true);
    }
  };
  const [playHover, setPlayHover] = useState(false);

  // eslint-disable-next-line no-nested-ternary -- Grandfathered violation
  const paddingTop = card.imageUrl ? '$4x' : cardVerticalAlignment === 'center' ? '$12x' : '$44x',
    paddingBottom = '$12x';

  const { imageLayout = 'top', backgroundImageSize = 'cover' } = card;

  return (
    <Box
      $platform-web={{
        minHeight,
      }}
    >
      <Link
        position="relative"
        display="flex"
        flexDirection="column"
        width={'100%'}
        height="100%"
        overflow="hidden"
        cursor="pointer"
        borderTopRightRadius={isArched ? '$full' : `$12x`}
        borderTopLeftRadius={isArched ? '$full' : `$12x`}
        borderBottomLeftRadius="$12x"
        borderBottomRightRadius="$12x"
        paddingTop={
          imageLayout === 'top' && backgroundImageSize !== 'contain' ? paddingTop : paddingBottom
        }
        paddingLeft="$12x"
        paddingRight="$12x"
        paddingBottom={
          imageLayout === 'top' && backgroundImageSize !== 'contain' ? paddingBottom : paddingTop
        }
        aria-label={card.ctaSheetLabel || 'Open dialog'}
        analyticsName={`cosmos-landing-page-multicard-${card.analyticsId}`}
        outlineColor="unset"
        href={!card.ctaSheet && card.redirectUrl ? card.redirectUrl : undefined}
        onPress={() => card.ctaSheet && setIsVisible(true)}
        onPressIn={onKeyDown as any}
        ref={ctaRef}
        outlineWidth={0}
        tabIndex={0}
        backgroundColor={(card.backgroundColor as any) || '$backgroundTertiary'}
        onMouseEnter={() => setPlayHover(true)}
        onMouseLeave={() => setPlayHover(false)}
      >
        <MulticardCardDetails
          {...card}
          isArched={isArched}
          componentTheme={componentTheme}
          cardVerticalAlignment={cardVerticalAlignment}
          analyticsId={card.analyticsId}
          columnNumber={columnNumber}
        />
        <Box
          position="absolute"
          width="100%"
          height="100%"
          scale={playHover ? 2 : 0}
          opacity={0.08}
          bottom={-200}
          left={0}
          zIndex={5}
          key="my-square"
          animation="decelerateFast"
          borderTopRightRadius="$full"
          borderTopLeftRadius="$full"
          transformOrigin="center bottom"
          backgroundColor={'$backgroundInverseStateHoverSecondary'}
        ></Box>
      </Link>
      {card.ctaSheet && (
        <Sheet
          entry={card.ctaSheet}
          isVisible={isVisible}
          setIsVisible={setIsVisible}
          exitComplete={exitComplete}
        />
      )}
    </Box>
  );
};

const AccordionDetails = ({ title, desc }: { title: string; desc: string }) => {
  return (
    <Accordion
      variant="regular"
      size="medium"
      isFullWidth
      analyticsName="cosmos-multicard-accordion"
      items={[
        {
          title,
          description: desc ? (
            <Text typography="$bodyMedium" tag="p" color="$contentPrimary">
              {markdownToReactElement(desc)}
            </Text>
          ) : (
            ''
          ),
        },
      ]}
    />
  );
};

const MulticardCardDetails = ({
  eyebrow,
  imageUrl,
  title,
  subhead,
  desc,
  ctaDisplayText,
  ctaStyle,
  isArched,
  isAccordion,
  componentTheme,
  redirectUrl,
  analyticsId,
  columnNumber,
  titleSize,
  imageLayout = 'top',
  backgroundImageSize = 'cover',
}: IMulticardCardDetails) => {
  const maxCardTitleLen = 48;
  const truncatedTitle =
    (title && title.length > maxCardTitleLen ? `${title.slice(0, maxCardTitleLen)}...` : title) ??
    '';
  const titleSizeToken = TitleSizeTokens[titleSize || 'large'];

  return (
    <Box flexDirection={imageLayout === 'bottom' ? 'column-reverse' : 'column'}>
      {imageUrl &&
        (redirectUrl ? (
          <Link
            href={isAccordion ? redirectUrl : undefined}
            aria-label={title || ''}
            analyticsName={`cosmos-landing-page-multicard-${analyticsId}`}
          >
            <CardImage
              isArched={!!isArched}
              src={imageUrl}
              alt={title || 'Card Image'}
              flex={1}
              zIndex={5}
              mb={imageLayout === 'top' && backgroundImageSize !== 'contain' ? '$16x' : undefined}
              columnNumber={columnNumber}
              backgroundImageSize={backgroundImageSize}
            />
          </Link>
        ) : (
          <CardImage
            isArched={!!isArched}
            src={imageUrl}
            alt={title || ''}
            flex={1}
            zIndex={5}
            mb={imageLayout === 'top' && backgroundImageSize !== 'contain' ? '$16x' : undefined}
            columnNumber={columnNumber}
            backgroundImageSize={backgroundImageSize}
          />
        ))}
      <Box flex={1}>
        {isAccordion ? (
          <AccordionDetails title={truncatedTitle} desc={desc || ''} />
        ) : (
          <>
            <Box zIndex={5} display="flex">
              {eyebrow && <Eyebrow mb={2}>{eyebrow}</Eyebrow>}
              <Text
                typography={titleSizeToken}
                fontWeight="$medium"
                tag="h3"
                color="$contentPrimary"
                mb={5}
              >
                {truncatedTitle}
              </Text>
            </Box>
            {subhead && (
              <Text tag="p" typography="$bodyMedium" color="$contentPrimary" fontWeight="$medium">
                {subhead}
              </Text>
            )}
            {desc && (
              <Text typography="$bodyMedium" tag="p" color="$contentPrimary">
                {markdownToReactElement(desc)}
              </Text>
            )}
            {ctaDisplayText && (
              <LPFauxBtn
                componentTheme={componentTheme}
                ctaDisplayText={ctaDisplayText}
                ctaStyle={ctaStyle}
                mt={6}
              />
            )}
          </>
        )}
      </Box>
    </Box>
  );
};

export default MulticardCard;
